/**
 * Restricted expression evaluator for schema-driven visibility/disabled gates.
 *
 * Grammar: <param-name> <op> <literal>
 *   op ∈ { ==, !=, <, <=, >, >=, in }
 *   "in" literal is pipe-separated, e.g. "use_case in text|mixed"
 *   Other literals are numbers (parsed as Number) or bare strings.
 *
 * NOT supported: arithmetic, logical ops, nested expressions. Keep it simple.
 */
export function evalExpr(
  expr: string | null | undefined,
  params: Record<string, unknown>,
): boolean {
  if (!expr) return false;

  // "in" grammar: name in a|b|c
  const inMatch = expr.match(/^\s*([a-zA-Z_][a-zA-Z0-9_]*)\s+in\s+(.+?)\s*$/);
  if (inMatch) {
    const [, name, rhs] = inMatch;
    const lhs = params[name];
    const options = rhs.split('|').map((s) => s.trim());
    return options.includes(String(lhs));
  }

  // Comparison grammar: name <op> rhs
  const m = expr.match(/^\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*(==|!=|<=|>=|<|>)\s*(.+?)\s*$/);
  if (!m) return false;
  const [, name, op, rhsRaw] = m;
  const rhsTrim = rhsRaw.trim();
  const rhsNumber = Number(rhsTrim);
  const rhs: unknown = isNaN(rhsNumber)
    ? rhsTrim.replace(/^['"]|['"]$/g, '')
    : rhsNumber;
  const lhs = params[name];
  // Booleans arrive from the backend as real booleans but are written in the
  // schema as `flag == true`, so compare them stringwise.
  if (typeof lhs === 'boolean') {
    const rhsBool = String(rhs) === 'true';
    switch (op) {
      case '==': return lhs === rhsBool;
      case '!=': return lhs !== rhsBool;
      default: return false;
    }
  }
  switch (op) {
    case '==': return lhs === rhs;
    case '!=': return lhs !== rhs;
    case '<': return Number(lhs) < Number(rhs);
    case '<=': return Number(lhs) <= Number(rhs);
    case '>': return Number(lhs) > Number(rhs);
    case '>=': return Number(lhs) >= Number(rhs);
    default: return false;
  }
}

/**
 * Visibility gate: `visible_when` empty means "always visible".
 * Non-empty is evaluated through evalExpr.
 */
export function isVisible(
  visibleWhen: string | null | undefined,
  params: Record<string, unknown>,
): boolean {
  if (!visibleWhen) return true;
  return evalExpr(visibleWhen, params);
}
